import { useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Done } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useFormContext, useWatch } from 'react-hook-form';
import { usePortalMotion } from '../../../../hooks/portal/usePortalMotion';
import { useLocalTranslation } from '../../../../utils/i18nKit';
import IntroButton from './IntroButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var EASE = [0.22, 1, 0.36, 1];

var IntroHeroSection = function IntroHeroSection(props) {
  var onLogin = props.onLogin,
      onGetAccess = props.onGetAccess;

  var _usePortalMotion = usePortalMotion(),
      motionEnabled = _usePortalMotion.motionEnabled;

  var _useAuth = useAuth0(),
      isAuthenticated = _useAuth.isAuthenticated;

  var _useLocalTranslation = useLocalTranslation(),
      $t = _useLocalTranslation.$t;

  var containerRef = useRef(null);

  var _useScroll = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  }),
      scrollYProgress = _useScroll.scrollYProgress;

  var imageY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  var _useFormContext = useFormContext(),
      control = _useFormContext.control;
  /**
   * watch
   */


  var heroTitle = useWatch({
    control: control,
    name: 'heroTitle'
  });
  var heroSubtitle = useWatch({
    control: control,
    name: 'heroSubtitle'
  });
  var heroChecklist = useWatch({
    control: control,
    name: 'heroChecklist'
  });
  var heroImageUrl = useWatch({
    control: control,
    name: 'heroImageUrl'
  });
  var titleWords = heroTitle.split(' ');
  /**
   * methods
   */

  var loginAction = function loginAction() {
    onLogin === null || onLogin === void 0 ? void 0 : onLogin();
  };

  var getAccessAction = function getAccessAction() {
    onGetAccess === null || onGetAccess === void 0 ? void 0 : onGetAccess();
  };

  var renderChecklist = function renderChecklist() {
    return /*#__PURE__*/_jsx(Box, {
      className: "flex flex-col space-y-5",
      children: heroChecklist.map(function (item) {
        return /*#__PURE__*/_jsxs(Box, {
          className: "flex flex-row items-center space-x-4",
          children: [/*#__PURE__*/_jsx(Box, {
            className: "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#006DB029] md:h-10 md:w-10",
            children: /*#__PURE__*/_jsx(Done, {
              className: "h-6 w-6 fill-[#006DB0]"
            })
          }), /*#__PURE__*/_jsx(Typography, {
            className: "text-base font-bold text-[#202020] md:text-lg",
            children: item
          })]
        }, item);
      })
    });
  };

  var renderActions = function renderActions() {
    return /*#__PURE__*/_jsxs(Box, {
      className: "flex flex-col space-y-6",
      children: [/*#__PURE__*/_jsx(Box, {
        children: /*#__PURE__*/_jsx(IntroButton, {
          variant: "primary",
          onClick: loginAction,
          children: isAuthenticated ? $t('text:navigation.log_out') : $t('page:subBrokerPortal.log_in')
        })
      }), /*#__PURE__*/_jsxs(Typography, {
        className: "text-base font-medium text-[#202020] md:text-lg",
        children: [$t('page:subBrokerPortal.want_access_to_broker_platform'), ' ']
      })]
    });
  };

  var renderHeroImage = function renderHeroImage() {
    if (!heroImageUrl) return null;
    return /*#__PURE__*/_jsx(Box, {
      component: "img",
      src: heroImageUrl,
      alt: "",
      className: "aspect-[4/3] w-full rounded-[28px] object-cover"
    });
  };

  if (!motionEnabled) {
    return /*#__PURE__*/_jsxs(Box, {
      className: "flex w-full flex-col items-center gap-12 pb-16 pt-10 md:flex-row md:pb-24 md:pt-20",
      children: [/*#__PURE__*/_jsxs(Box, {
        className: "flex flex-1 flex-col space-y-8",
        children: [/*#__PURE__*/_jsx(Typography, {
          component: "h1",
          className: "font-['Playfair_Display',serif] text-4xl font-bold leading-[1.15] text-[#202020] md:text-[56px]",
          children: heroTitle
        }), /*#__PURE__*/_jsx(Typography, {
          className: "text-base leading-7 text-[#474747] md:text-lg md:leading-8",
          children: heroSubtitle
        }), renderChecklist(), renderActions()]
      }), heroImageUrl && /*#__PURE__*/_jsx(Box, {
        className: "w-full md:flex-1",
        children: renderHeroImage()
      })]
    });
  }

  return /*#__PURE__*/_jsx(Box, {
    ref: containerRef,
    className: "relative w-full overflow-hidden",
    children: /*#__PURE__*/_jsxs(Box, {
      className: "relative flex w-full flex-col items-center gap-12 pb-16 pt-10 md:flex-row md:pb-24 md:pt-20",
      children: [/*#__PURE__*/_jsxs(Box, {
        className: "flex flex-1 flex-col space-y-8",
        children: [/*#__PURE__*/_jsx(Typography, {
          component: "h1",
          className: "font-['Playfair_Display',serif] text-4xl font-black leading-[1.15] text-[#202020] md:text-[52px]",
          children: titleWords.map(function (word, index) {
            return /*#__PURE__*/_jsx(motion.span, {
              className: "mr-[0.28em] inline-block font-['Playfair_Display',serif]",
              initial: {
                opacity: 0,
                y: 28,
                filter: 'blur(6px)'
              },
              animate: {
                opacity: 1,
                y: 0,
                filter: 'blur(0px)'
              },
              transition: {
                duration: 0.55,
                delay: 0.12 + index * 0.05,
                ease: EASE
              },
              children: word
            }, "".concat(word, "-").concat(index));
          })
        }), /*#__PURE__*/_jsx(motion.div, {
          initial: {
            opacity: 0,
            y: 24
          },
          animate: {
            opacity: 1,
            y: 0
          },
          transition: {
            duration: 0.7,
            delay: 0.35,
            ease: EASE
          },
          children: /*#__PURE__*/_jsx(Typography, {
            className: "text-base font-medium leading-6 text-[#474747] md:text-lg md:leading-7",
            children: heroSubtitle
          })
        }), /*#__PURE__*/_jsx(motion.div, {
          initial: {
            opacity: 0,
            y: 24
          },
          animate: {
            opacity: 1,
            y: 0
          },
          transition: {
            duration: 0.7,
            delay: 0.45,
            ease: EASE
          },
          children: renderChecklist()
        }), /*#__PURE__*/_jsx(motion.div, {
          initial: {
            opacity: 0,
            y: 24
          },
          animate: {
            opacity: 1,
            y: 0
          },
          transition: {
            duration: 0.7,
            delay: 0.55,
            ease: EASE
          },
          children: renderActions()
        })]
      }), heroImageUrl && /*#__PURE__*/_jsx(motion.div, {
        className: "w-full md:flex-1",
        style: {
          y: imageY
        },
        initial: {
          opacity: 0,
          scale: 0.94
        },
        animate: {
          opacity: 1,
          scale: 1
        },
        transition: {
          duration: 0.9,
          delay: 0.25,
          ease: EASE
        },
        children: renderHeroImage()
      })]
    })
  });
};

export default IntroHeroSection;